import { Alert, Box, Card, CardContent, CircularProgress, Grid, Stack, Typography } from '@mui/material'
import { EmissionTier, MetricState } from '../common'
import { usePropertyClimateData } from '../../../hooks/climateData'
import { useCurrentUser } from '../../../hooks/users'
import { ExtremeWindScoreBadge } from '../common/extremeWind/utils'
import ExtremeWindSpeedTrends from './extremeWind/ExtremeWindSpeedTrends'
import ClimateMetricCard from '../common/ClimateMetricCard'
import ClimateSectionTitle from '../common/ClimateSectionTitle'
import { renderHelpLines } from '../common/aggregateHelpText'
import {
  BI_DAYS_LABEL,
  DAMAGE_AAL_LABEL,
  FINANCIAL_BREAKDOWN_TITLE,
  formatCurrencyValue,
  formatPlainNumber,
  parseMetricPointY,
  REVENUE_AAL_LABEL,
  sumNumbers,
  TOTAL_AAL_LABEL,
} from '../common/financialMetrics'

const SCORE_METRIC_KEY = 'tc:score'
const DAMAGE_AAL_METRIC_KEY = 'tc:average_damage_loss'
const REVENUE_AAL_METRIC_KEY = 'tc:average_bi_loss'
const BI_DAYS_METRIC_KEY = 'tc:average_bi_days'

const PropertyExtremeWind: React.FC<{ propertyId: number; emissionTier: EmissionTier }> = ({
  propertyId,
  emissionTier,
}) => {
  if (!propertyId) {
    return <Alert severity="info">Select a property to view extreme wind metrics.</Alert>
  }

  const {
    data: scoreData,
    isLoading: scoreLoading,
    error: scoreError,
  } = usePropertyClimateData(SCORE_METRIC_KEY, { propertyId })
  const {
    data: damageAalData,
    isLoading: damageAalLoading,
    error: damageAalError,
  } = usePropertyClimateData(DAMAGE_AAL_METRIC_KEY, { propertyId })
  const {
    data: revenueAalData,
    isLoading: revenueAalLoading,
    error: revenueAalError,
  } = usePropertyClimateData(REVENUE_AAL_METRIC_KEY, { propertyId })
  const {
    data: biDaysData,
    isLoading: biDaysLoading,
    error: biDaysError,
  } = usePropertyClimateData(BI_DAYS_METRIC_KEY, { propertyId })

  const { currencySymbol } = useCurrentUser()

  const loading = scoreLoading || damageAalLoading || revenueAalLoading || biDaysLoading
  const allErrored = !!(scoreError && damageAalError && revenueAalError && biDaysError)
  const hasData = !!(scoreData || damageAalData || revenueAalData || biDaysData)

  if (loading && !hasData) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={160}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (allErrored) {
    return <Alert severity="error">Unable to load extreme wind metrics for this property.</Alert>
  }

  if (!hasData) {
    return <Alert severity="info">No extreme wind data is available for this property.</Alert>
  }

  const scoreValue =
    scoreData?.state?.value === MetricState.SUCCEEDED ? parseMetricPointY(scoreData.data?.data?.[emissionTier]) : null
  const damageAalValue =
    damageAalData?.state?.value === MetricState.SUCCEEDED
      ? parseMetricPointY(damageAalData.data?.data?.[emissionTier])
      : null
  const revenueAalValue =
    revenueAalData?.state?.value === MetricState.SUCCEEDED
      ? parseMetricPointY(revenueAalData.data?.data?.[emissionTier])
      : null
  const biDaysValue =
    biDaysData?.state?.value === MetricState.SUCCEEDED ? parseMetricPointY(biDaysData.data?.data?.[emissionTier]) : null

  const totalAalValue = sumNumbers([damageAalValue, revenueAalValue])
  const totalAalHelpText = renderHelpLines([
    `${TOTAL_AAL_LABEL} = Damage AAL + BI AAL.`,
    'Extreme wind uses a single long-term horizon.',
  ])

  return (
    <Stack spacing={4}>
      <Card>
        <CardContent>
          <Box mb={3}>
            <ClimateSectionTitle
              title="Overview"
              subtitle="Summary of long-term extreme wind risk"
              helpText="Extreme wind metrics use the long-term horizon shown in the page header."
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <ClimateMetricCard
                title="Risk Rating"
                variant="outlined"
                helpText="Extreme wind risk rating for the long-term horizon."
              >
                {scoreValue != null ? (
                  <ExtremeWindScoreBadge score={scoreValue} size="regular" />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Extreme wind risk rating is unavailable.
                  </Typography>
                )}
              </ClimateMetricCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <ClimateMetricCard title={TOTAL_AAL_LABEL} variant="outlined" helpText={totalAalHelpText}>
                {totalAalValue != null ? (
                  <Typography variant="h3">{formatCurrencyValue(totalAalValue, currencySymbol, 0)}</Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Total AAL data is unavailable.
                  </Typography>
                )}
              </ClimateMetricCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <ClimateMetricCard
                title={BI_DAYS_LABEL}
                variant="outlined"
                helpText="Expected business interruption days per year for the long-term horizon."
              >
                {biDaysValue != null ? (
                  <Typography variant="h3">{`${formatPlainNumber(biDaysValue, 1)} days/year`}</Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Business interruption days are unavailable.
                  </Typography>
                )}
              </ClimateMetricCard>
            </Grid>
          </Grid>

          <Box mt={4}>
            <ClimateSectionTitle
              title={FINANCIAL_BREAKDOWN_TITLE}
              subtitle="Damage and revenue components of annualised extreme wind impacts"
              helpText="Financial metrics are annualised expected losses for the long-term horizon."
            />
          </Box>

          <Grid container spacing={3} mt={0.5}>
            <Grid item xs={12} md={6}>
              <ClimateMetricCard
                title={DAMAGE_AAL_LABEL}
                variant="outlined"
                helpText="Annualised structure damage loss for the long-term horizon."
              >
                {damageAalValue != null ? (
                  <Typography variant="h3">{formatCurrencyValue(damageAalValue, currencySymbol, 0)}</Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Damage AAL is unavailable.
                  </Typography>
                )}
              </ClimateMetricCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <ClimateMetricCard
                title={REVENUE_AAL_LABEL}
                variant="outlined"
                helpText="Annualised business interruption revenue loss for the long-term horizon."
              >
                {revenueAalValue != null ? (
                  <Typography variant="h3">{formatCurrencyValue(revenueAalValue, currencySymbol, 0)}</Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    BI AAL is unavailable.
                  </Typography>
                )}
              </ClimateMetricCard>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <ExtremeWindSpeedTrends propertyId={propertyId} emissionTier={emissionTier} />
        </CardContent>
      </Card>
    </Stack>
  )
}

export default PropertyExtremeWind
